import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { UNITS } from "@/data/units";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const Header = () => {
  const { t } = useLang();
  const location = useLocation();
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatalogOpen, setMobileCatalogOpen] = useState(false);
  const dropdownRef = useRef(null);
  const hoverTimer = useRef(null);

  // Close on route change
  useEffect(() => {
    setCatalogOpen(false);
    setMobileOpen(false);
    setMobileCatalogOpen(false);
  }, [location.pathname]);

  // Click outside to close
  useEffect(() => {
    const onDoc = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCatalogOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onCatalogEnter = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setCatalogOpen(true);
  };
  const onCatalogLeave = () => {
    hoverTimer.current = setTimeout(() => setCatalogOpen(false), 120);
  };

  const navItems = [
    { key: "home", label: t.nav.home, to: "/" },
    { key: "about", label: t.nav.about, to: "/about" },
  ];
  const tailItems = [{ key: "contact", label: t.nav.contact, to: "/contact" }];

  return (
    <header
      data-testid="site-header"
      className="emka-header fixed top-0 left-0 right-0 z-50"
    >
      {/* Top language bar */}
      <div className="w-full border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-9 flex items-center justify-end">
          <LanguageSwitcher />
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Logo />

        {/* Desktop nav */}
        <nav
          data-testid="desktop-nav"
          className="hidden lg:flex items-center gap-8"
        >
          {navItems.map((item) => (
            <Link
              key={item.key}
              data-testid={`nav-${item.key}`}
              to={item.to}
              className="text-white/85 hover:text-white text-sm font-semibold uppercase tracking-wider transition-colors"
            >
              {item.label}
            </Link>
          ))}

          {/* Catalogs dropdown */}
          <div
            ref={dropdownRef}
            onMouseEnter={onCatalogEnter}
            onMouseLeave={onCatalogLeave}
            className="relative"
          >
            <button
              type="button"
              data-testid="nav-catalogs"
              onClick={() => setCatalogOpen(true)}
              aria-haspopup="menu"
              aria-expanded={catalogOpen}
              className="flex items-center gap-1.5 text-white/85 hover:text-white text-sm font-semibold uppercase tracking-wider transition-colors"
            >
              {t.nav.catalogs}
              <ChevronDown
                size={14}
                className={`transition-transform ${catalogOpen ? "rotate-180" : ""}`}
              />
            </button>

            {catalogOpen && (
              <div
                data-testid="catalogs-dropdown"
                role="menu"
                className="absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[240px] rounded-md border border-white/10 shadow-2xl"
                style={{ backgroundColor: "#0d1430" }}
              >
                <div
                  className="h-[2px] w-full rounded-t"
                  style={{ backgroundColor: "var(--emka-gold)" }}
                />
                <ul className="py-2">
                  {UNITS.map((u) => (
                    <li key={u.slug}>
                      <Link
                        to={`/${u.slug}`}
                        data-testid={`catalog-item-${u.slug}`}
                        className="flex items-center justify-between gap-3 px-4 py-2.5 text-white/85 hover:text-white text-[13px] font-medium tracking-wide hover:bg-white/5"
                        role="menuitem"
                      >
                        <span>{t.catalogs[u.key]}</span>
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: u.accent }}
                        />
                      </Link>
                    </li>
                  ))}
                  <li
                    className="mt-1 pt-2 border-t border-white/8 px-4 pb-1 text-[10px] uppercase tracking-[0.24em]"
                    style={{ color: "var(--emka-gold)" }}
                  >
                    {t.catalogs.brochuresLabel}
                  </li>
                  <li>
                    <Link
                      to="/catalogs/urban"
                      data-testid="catalog-item-brochure-urban"
                      className="flex items-center gap-3 px-4 py-2.5 text-white/85 hover:text-white text-[13px] font-medium tracking-wide hover:bg-white/5"
                      role="menuitem"
                    >
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: "#7ec043" }}
                      />
                      <span>{t.catalogs.brochureUrban}</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/catalogs/lighting"
                      data-testid="catalog-item-brochure-lighting"
                      className="flex items-center gap-3 px-4 py-2.5 text-white/85 hover:text-white text-[13px] font-medium tracking-wide hover:bg-white/5"
                      role="menuitem"
                    >
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: "#d4a249" }}
                      />
                      <span>{t.catalogs.brochureLighting}</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/catalogs/heating"
                      data-testid="catalog-item-brochure-heating"
                      className="flex items-center gap-3 px-4 py-2.5 text-white/85 hover:text-white text-[13px] font-medium tracking-wide hover:bg-white/5"
                      role="menuitem"
                    >
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: "#e07b3c" }}
                      />
                      <span>{t.catalogs.brochureHeating}</span>
                    </Link>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {tailItems.map((item) => (
            <Link
              key={item.key}
              data-testid={`nav-${item.key}`}
              to={item.to}
              className="text-white/85 hover:text-white text-sm font-semibold uppercase tracking-wider transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Mobile menu button */}
        <button
          type="button"
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-white/10 text-white hover:bg-white/5"
          aria-label="Toggle navigation"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-nav"
          className="lg:hidden border-t border-white/5"
          style={{ backgroundColor: "#0a0f23" }}
        >
          <nav className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                data-testid={`mobile-nav-${item.key}`}
                className="py-3 text-white/90 text-sm font-semibold uppercase tracking-wider border-b border-white/5"
              >
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              data-testid="mobile-nav-catalogs"
              onClick={() => setMobileCatalogOpen((v) => !v)}
              className="flex items-center justify-between py-3 text-white/90 text-sm font-semibold uppercase tracking-wider border-b border-white/5"
              aria-expanded={mobileCatalogOpen}
            >
              <span>{t.nav.catalogs}</span>
              <ChevronDown
                size={16}
                className={`transition-transform ${mobileCatalogOpen ? "rotate-180" : ""}`}
              />
            </button>
            {mobileCatalogOpen && (
              <ul className="pl-3 py-1 border-b border-white/5">
                {UNITS.map((u) => (
                  <li key={u.slug}>
                    <Link
                      to={`/${u.slug}`}
                      data-testid={`mobile-catalog-${u.slug}`}
                      className="flex items-center gap-3 py-2.5 text-white/75 text-[13px]"
                    >
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: u.accent }}
                      />
                      {t.catalogs[u.key]}
                    </Link>
                  </li>
                ))}
                <li
                  className="mt-2 pt-2 border-t border-white/8 text-[10px] uppercase tracking-[0.24em]"
                  style={{ color: "var(--emka-gold)" }}
                >
                  {t.catalogs.brochuresLabel}
                </li>
                <li>
                  <Link
                    to="/catalogs/urban"
                    data-testid="mobile-catalog-brochure-urban"
                    className="flex items-center gap-3 py-2.5 text-white/75 text-[13px]"
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: "#7ec043" }}
                    />
                    {t.catalogs.brochureUrban}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/catalogs/lighting"
                    data-testid="mobile-catalog-brochure-lighting"
                    className="flex items-center gap-3 py-2.5 text-white/75 text-[13px]"
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: "#d4a249" }}
                    />
                    {t.catalogs.brochureLighting}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/catalogs/heating"
                    data-testid="mobile-catalog-brochure-heating"
                    className="flex items-center gap-3 py-2.5 text-white/75 text-[13px]"
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: "#e07b3c" }}
                    />
                    {t.catalogs.brochureHeating}
                  </Link>
                </li>
              </ul>
            )}
            {tailItems.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                data-testid={`mobile-nav-${item.key}`}
                className="py-3 text-white/90 text-sm font-semibold uppercase tracking-wider"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
